/**
 * GMP Data Mapper
 * Normalizes items from GET /api/market/gmp into display-ready rows.
 * The source is a live scrape of an external grey-market tracker, so any field
 * may be null, a placeholder like "-", a plain number or a formatted string.
 */

const PLACEHOLDERS = ['', '-', '--', '—', 'na', 'n/a', 'null'];

export const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && PLACEHOLDERS.includes(value.trim().toLowerCase()));

export const cleanText = (value) => (isBlank(value) ? null : String(value).trim());

// First signed number in a value: 25, "25", "₹-12.5", "−8 (3%)" -> 25, 25, -12.5, -8
export const toSignedNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (isBlank(value)) return null;
  const match = String(value).replace(/,/g, '').replace(/−/g, '-').match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
};

// Upper end of a price that may be a band: 99, "₹99", "₹94 - ₹99" -> 99
export const toUpperPrice = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (isBlank(value)) return null;
  const matches = String(value).replace(/,/g, '').match(/\d+(\.\d+)?/g);
  return matches ? Math.max(...matches.map(Number)) : null;
};

// URL slug for /ipo-gmp/:companySlug — the company GMP endpoint accepts it as-is
// ("Maharaja & Speedex India" -> "maharaja-speedex-india")
export const toGmpSlug = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Dates can carry that day's GMP from the source: "17-Sep GMP: 30" -> { date: '17-Sep', gmp: 30 }
export const splitDateField = (value) => {
  const text = cleanText(value);
  if (!text) return { date: null, gmp: null };
  const [date, gmpPart] = text.split(/\s*GMP\s*:\s*/i);
  return {
    date: cleanText(date),
    gmp: gmpPart !== undefined ? toSignedNumber(gmpPart) : null,
  };
};

const formatAmount = (n) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const formatSignedRupee = (n) => {
  if (n > 0) return `+₹${formatAmount(n)}`;
  if (n < 0) return `−₹${formatAmount(Math.abs(n))}`;
  return '₹0';
};

const formatSignedPercent = (n) => {
  if (n > 0) return `+${formatAmount(n)}%`;
  if (n < 0) return `−${formatAmount(Math.abs(n))}%`;
  return '0%';
};

const formatRupeeOrRaw = (value) => {
  if (isBlank(value)) return null;
  if (typeof value === 'number') return `₹${formatAmount(value)}`;
  const text = String(value).trim();
  return /^\d[\d,]*(\.\d+)?$/.test(text) ? `₹${text}` : text;
};

const trendOf = (n) => {
  if (n === null) return null;
  if (n > 0) return 'up';
  if (n < 0) return 'down';
  return 'flat';
};

export const mapGmpItem = (item, index = 0) => {
  if (!item) return null;

  const companyName = cleanText(item.company_name) || 'Unnamed IPO';
  const board = cleanText(item.board);
  const status = cleanText(item.status);

  const gmpValue = toSignedNumber(item.gmp_value);
  const priceValue = toUpperPrice(item.price);

  // Prefer the source's percentage; derive it from price only when missing
  let gmpPercent = toSignedNumber(item.gmp_percent);
  if (gmpPercent === null && gmpValue !== null && priceValue) {
    gmpPercent = Math.round((gmpValue / priceValue) * 10000) / 100;
  }

  let gmpDisplay = null;
  if (gmpValue !== null) {
    gmpDisplay = formatSignedRupee(gmpValue);
  } else if (!isBlank(item.gmp_value)) {
    gmpDisplay = String(item.gmp_value).trim();
  }

  const dayLow = toSignedNumber(item.gmp_day_low);
  const dayHigh = toSignedNumber(item.gmp_day_high);
  const dayRangeDisplay =
    dayLow !== null || dayHigh !== null
      ? `${dayLow !== null ? formatSignedRupee(dayLow) : '—'} / ${
          dayHigh !== null ? formatSignedRupee(dayHigh) : '—'
        }`
      : null;

  const estListingValue =
    gmpValue !== null && priceValue !== null ? priceValue + gmpValue : null;

  return {
    key: `${companyName}-${index}`,
    slug: cleanText(item.company_name) ? toGmpSlug(item.company_name) : null,
    companyName,
    exchange: cleanText(item.exchange),
    board,
    isSme: (board || '').toUpperCase().includes('SME'),
    status,
    statusKey: (status || 'unknown').toLowerCase(),
    rating: cleanText(item.rating),

    gmpValue,
    gmpDisplay,
    gmpPercent,
    gmpPercentDisplay: gmpPercent !== null ? formatSignedPercent(gmpPercent) : null,
    trend: trendOf(gmpValue),
    dayRangeDisplay,

    priceDisplay: formatRupeeOrRaw(item.price),
    estListingDisplay: estListingValue !== null ? `₹${formatAmount(estListingValue)}` : null,

    subscription: cleanText(item.subscription),
    issueSize: cleanText(item.issue_size),
    lotSize: cleanText(item.lot_size),
    openDate: splitDateField(item.open_date).date,
    closeDate: splitDateField(item.close_date).date,
    boaDate: splitDateField(item.boa_date).date,
    listingDate: splitDateField(item.listing_date).date,
    updatedOn: cleanText(item.source_updated_on),
  };
};
